// useBulkUpdate.ts
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateClients } from "../api";
import { notifications } from "@mantine/notifications";

interface BulkUpdateStatusPayload {
  clientIds: string[];
  status: string;
  taxYear?: number;
}

interface BulkUpdateAssigneePayload {
  clientIds: string[];
  memberId: string | null;
}

/**
 * Hook for performing bulk updates on clients (status, assignee, etc.)
 */
export const useBulkUpdate = () => {
  const queryClient = useQueryClient();

  // Bulk status update mutation
  const bulkStatusMutation = useMutation<
    { success: number; failed: number },
    Error,
    BulkUpdateStatusPayload
  >({
    mutationFn: async ({ clientIds, status, taxYear }) => {
      console.log(
        `Starting bulk status update for ${clientIds.length} clients to status: ${status}${taxYear ? ` for tax year ${taxYear}` : ""}`
      );

      // Use Promise.allSettled to ensure all requests are attempted
      // even if some fail, so we can report partial success
      const updatePromises = clientIds.map(async (clientId) => {
        try {
          const updateData: any = { status };
          if (taxYear) {
            updateData.taxYear = taxYear;
          }
          const result = await updateClients(updateData, clientId);
          console.log(`Successfully updated client ${clientId}`);
          return { success: true, clientId, result };
        } catch (error) {
          console.error(`Failed to update client ${clientId}:`, error);
          return { success: false, clientId, error };
        }
      });

      const results = await Promise.allSettled(updatePromises);

      let successCount = 0;
      let failedCount = 0;

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          if (result.value.success) {
            successCount++;
          } else {
            failedCount++;
          }
        } else {
          failedCount++;
          console.error(
            `Promise rejected for client ${clientIds[index]}:`,
            result.reason
          );
        }
      });

      console.log(
        `Bulk update completed: ${successCount} succeeded, ${failedCount} failed out of ${clientIds.length} total`
      );

      if (failedCount > 0) {
        console.warn(
          `Bulk update: ${successCount} succeeded, ${failedCount} failed`
        );
      }

      return { success: successCount, failed: failedCount };
    },
    onSuccess: async (data) => {
      // Batch invalidate all queries at once to trigger single refetch
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return key === "clients" || key === "search" || key === "analytics";
        },
      });

      // Show success notification even if some failed
      if (data.failed > 0) {
        notifications.show({
          title: "Partial Success",
          message: `Updated ${data.success} client(s). ${data.failed} failed.`,
          color: "yellow",
        });
      }
    },
    onError: (error) => {
      console.error("Error updating clients:", error);
      notifications.show({
        title: "Error",
        message: "Failed to update clients. Please try again.",
        color: "red",
      });
    },
  });

  // Bulk assignee update mutation
  const bulkAssigneeMutation = useMutation<
    void,
    Error,
    BulkUpdateAssigneePayload
  >({
    mutationFn: async ({ clientIds, memberId }) => {
      // Import assignClientsToMembers for bulk assignee updates
      const { assignClientsToMembers } = await import("../api");

      // Update each client's assignee using the assignment API
      const updatePromises = clientIds.map((clientId) =>
        assignClientsToMembers({
          clientId,
          memberId,
        })
      );

      await Promise.all(updatePromises);
    },
    onSuccess: async () => {
      // Batch invalidate all queries at once to trigger single refetch
      await queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];
          return (
            key === "clients" ||
            key === "search" ||
            key === "members" ||
            key === "client" ||
            key === "analytics"
          );
        },
      });
    },
    onError: (error) => {
      console.error("Error updating assignees:", error);
      notifications.show({
        title: "Error",
        message: "Failed to update some assignees. Please try again.",
        color: "red",
      });
    },
  });

  const bulkUpdateStatus = async (
    clientIds: string[],
    status: string,
    taxYear?: number
  ): Promise<{ success: number; failed: number }> => {
    return await bulkStatusMutation.mutateAsync({ clientIds, status, taxYear });
  };

  const bulkUpdateAssignee = async (
    clientIds: string[],
    memberId: string | null
  ) => {
    await bulkAssigneeMutation.mutateAsync({ clientIds, memberId });
  };

  return {
    bulkUpdateStatus,
    bulkUpdateAssignee,
    isUpdatingStatus: bulkStatusMutation.isPending,
    isUpdatingAssignee: bulkAssigneeMutation.isPending,
    updateStatusError: bulkStatusMutation.error,
    updateAssigneeError: bulkAssigneeMutation.error,
  };
};
